import { useState } from 'react';
import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  linkWithCredential,
  linkWithPopup,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { EmptyState } from './common';

export function NotificationCenter({ notifications, unreadCount, open, onToggle, onClose, onOpenPost, onRead, onMarkAllRead }) {
  const count = unreadCount || 0;

  const handleOpenNotification = (item) => {
    onRead(item.id);
    onOpenPost(item.post);
    onClose();
  };

  return (
    <div className="notification-wrap">
      <button className={`notif-bell ${open ? 'active' : ''}`} onClick={onToggle} aria-label="Notifications" title="Notifications">
        <span className="bell-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
            <path d="M10 21h4" />
          </svg>
        </span>
        {count > 0 && <strong>{count > 9 ? '9+' : count}</strong>}
      </button>
      {open && (
        <div className="notification-panel">
          <div className="notification-head">
            <span>Notifications</span>
            <div className="notification-head-actions">
              {count > 0 && <button onClick={onMarkAllRead}>Mark all read</button>}
              <button onClick={onClose}>Close</button>
            </div>
          </div>
          {notifications.length === 0 ? (
            <EmptyState
              compact
              eyebrow="Quiet"
              title="No notifications yet"
              body="Replies, followers, and activity on your Hixs will show here." />
          ) : notifications.map(item => (
            <button key={item.id} className={`notification-item ${item.read ? 'read' : 'unread'}`} onClick={() => handleOpenNotification(item)}>
              <span className={`notification-dot ${item.type}`}></span>
              <span>
                <strong>{item.title}</strong>
                <small>{item.body}</small>
                <em>{item.meta}</em>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ReportModal({ target, onClose, onSubmit }) {
  const [reason, setReason] = useState('Spam');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const reasons = ['Spam', 'Harassment', 'Hate', 'Violence', 'Misinformation', 'Nudity', 'Other'];

  if (!target) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({ reason, details: details.trim() });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="report-overlay" onClick={e => e.target.className === 'report-overlay' && onClose()}>
      <div className="report-modal">
        <div className="report-head">
          <div>
            <div className="report-title">Report {target.type === 'post' ? 'Hix' : 'comment'}</div>
            <div className="report-sub">Reports help keep Hixrs useful and respectful.</div>
          </div>
          <button onClick={onClose}>Close</button>
        </div>
        <div className="report-guidelines-note">
          Keep it opinionated, not abusive. No harassment, hate, threats, spam, or explicit content.
        </div>
        <div className="report-target">{target.preview}</div>
        <div className="report-reasons">
          {reasons.map(item => (
            <button key={item} className={reason === item ? 'selected' : ''} onClick={() => setReason(item)}>{item}</button>
          ))}
        </div>
        <textarea className="report-details" rows={3} placeholder="Add details, optional" value={details} maxLength={220} onChange={e => setDetails(e.target.value)} />
        <div className="report-actions">
          <button className="report-cancel" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="report-submit" onClick={submit} disabled={submitting}>{submitting ? 'Sending...' : 'Submit report'}</button>
        </div>
      </div>
    </div>
  );
}

export function ConfirmModal({ dialog, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  if (!dialog) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      await dialog.onConfirm?.();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="confirm-overlay" onClick={e => e.target.className === 'confirm-overlay' && !submitting && onClose()}>
      <div className={`confirm-modal ${dialog.variant || 'danger'}`}>
        <div className="confirm-title">{dialog.title}</div>
        <div className="confirm-message">{dialog.message}</div>
        <div className="confirm-actions">
          <button className={`confirm-primary ${dialog.variant || 'danger'}`} onClick={submit} disabled={submitting}>
            {submitting ? 'Working...' : dialog.confirmLabel || 'Confirm'}
          </button>
          <button className="confirm-cancel" onClick={onClose} disabled={submitting}>{dialog.cancelLabel || 'Cancel'}</button>
        </div>
      </div>
    </div>
  );
}

function getAuthMessage(error) {
  const code = error?.code || '';
  if (code === 'auth/invalid-email') return 'Enter a valid email address.';
  if (code === 'auth/missing-password') return 'Enter your password.';
  if (code === 'auth/weak-password') return 'Password should be at least 6 characters.';
  if (code === 'auth/email-already-in-use') return 'That email already has an account. Switch to sign in.';
  if (code === 'auth/user-not-found' || code === 'auth/wrong-password' || code === 'auth/invalid-credential') return 'Email or password is wrong.';
  if (code === 'auth/too-many-requests') return 'Too many attempts. Try again later.';
  if (code === 'auth/operation-not-allowed') return 'Email login is not enabled in Firebase yet.';
  return 'Could not complete email login. Try again.';
}

function getEmailDisplayName(email) {
  return String(email || '')
    .split('@')[0]
    .replace(/[^a-zA-Z0-9_.-]/g, '')
    .slice(0, 24) || 'Hixrs User';
}

export function LoginSheet({ onClose, title = 'Join Hixrs', subtitle = 'Sign in to lock in, comment, and build your opinion identity.', cta = 'Continue with Google' }) {
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMode, setEmailMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailNotice, setEmailNotice] = useState('');

  const handleGoogle = async () => {
    setLoading(true);
    setEmailError('');
    setEmailNotice('');
    try {
      const currentUser = auth.currentUser;
      if (currentUser?.isAnonymous) {
        try {
          await linkWithPopup(currentUser, googleProvider);
        } catch (e) {
          if (e.code === 'auth/credential-already-in-use' || e.code === 'auth/email-already-in-use') {
            await signInWithPopup(auth, googleProvider);
          } else {
            throw e;
          }
        }
      } else {
        await signInWithPopup(auth, googleProvider);
      }
      onClose({ signedIn: true });
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    setEmailLoading(true);
    setEmailError('');
    setEmailNotice('');

    try {
      const currentUser = auth.currentUser;
      if (emailMode === 'signup') {
        if (currentUser?.isAnonymous) {
          const credential = EmailAuthProvider.credential(cleanEmail, password);
          const linked = await linkWithCredential(currentUser, credential);
          if (!linked.user.displayName) {
            await updateProfile(linked.user, { displayName: getEmailDisplayName(cleanEmail) });
          }
        } else {
          const created = await createUserWithEmailAndPassword(auth, cleanEmail, password);
          await updateProfile(created.user, { displayName: getEmailDisplayName(cleanEmail) });
        }
      } else {
        await signInWithEmailAndPassword(auth, cleanEmail, password);
      }

      onClose({ signedIn: true });
    } catch (e) {
      console.error(e);
      setEmailError(getAuthMessage(e));
      setEmailLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setEmailError('Enter your email first.');
      return;
    }

    setEmailLoading(true);
    setEmailError('');
    setEmailNotice('');

    try {
      await sendPasswordResetEmail(auth, cleanEmail);
      setEmailNotice('Password reset email sent.');
    } catch (e) {
      console.error(e);
      setEmailError(getAuthMessage(e));
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className="sheet-overlay" onClick={e => e.target.className === 'sheet-overlay' && onClose()}>
      <div className="sheet">
        <div className="sheet-handle"></div>
        <div className="sheet-logo">hixrs<span>.</span></div>
        <div className="sheet-title">{title}</div>
        <div className="sheet-sub">{subtitle}</div>
        <button className="google-btn" onClick={handleGoogle} disabled={loading}>
          {loading ? 'Signing in...' : cta}
        </button>
        <div className="sheet-divider"><span>or use email</span></div>
        <div className="email-auth-tabs" role="tablist" aria-label="Email auth mode">
          <button type="button" className={emailMode === 'signin' ? 'active' : ''} onClick={() => { setEmailMode('signin'); setEmailError(''); setEmailNotice(''); }}>Sign in</button>
          <button type="button" className={emailMode === 'signup' ? 'active' : ''} onClick={() => { setEmailMode('signup'); setEmailError(''); setEmailNotice(''); }}>Create</button>
        </div>
        <form className="email-auth-form" onSubmit={handleEmailSubmit}>
          <input
            type="email"
            autoComplete="email"
            placeholder="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={emailLoading}
            required
          />
          <input
            type="password"
            autoComplete={emailMode === 'signin' ? 'current-password' : 'new-password'}
            placeholder={emailMode === 'signin' ? 'Password' : 'Password, 6+ characters'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={emailLoading}
            minLength={6}
            required
          />
          {emailError && <div className="email-auth-message error">{emailError}</div>}
          {emailNotice && <div className="email-auth-message notice">{emailNotice}</div>}
          <button className="email-auth-submit" type="submit" disabled={emailLoading}>
            {emailLoading ? 'Working...' : emailMode === 'signin' ? 'Sign in with email' : 'Create email account'}
          </button>
        </form>
        {emailMode === 'signin' && (
          <button className="forgot-password-btn" onClick={handlePasswordReset} disabled={emailLoading}>
            Forgot password?
          </button>
        )}
        <button className="sheet-cancel" onClick={onClose}>Maybe later</button>
      </div>
    </div>
  );
}

export function Toast({ message }) {
  if (!message) return null;
  return <div className="toast">{message}</div>;
}
